<script setup>
  import { computed } from "vue";

  const props = defineProps({
    containerClass: {
      type: String,
      default: "",
    },
    align: {
      type: String,
      default: "left", // center | left
    },
    total: {
      type: Number,
    },
    totalCountUnit: {
      type: String,
      default: "건",
    },
    /**variant
     * list: 최상단 primary line, 세로줄 없음, 목록형
     * bordered: 가로/세로줄 있음, 일반 테이블
     * answer: 정답테이블
     */
    variant: {
      type: String,
      default: "list", // list | bordered | answer
    },
    clickable: {
      type: Boolean,
      default: false,
    },
  });

  const tableStyle = computed(() => {
    const baseStyle = {
      "--n-font-size": "1rem",
      "--n-line-height": "1.25",
      "--n-td-text-color": "var(--smoe-gray-800)",
      "--n-th-padding": "0.75rem",
      "--n-th-text-color": "var(--smoe-gray-800)",
      "table-layout": "fixed",
    };

    switch (props.variant) {
      case "bordered":
        return {
          ...baseStyle,
          "--n-border-color-modal": "var(--smoe-border)",
          "--n-border-color": "var(--smoe-border)",
          "--n-border-radius": "0.5rem",
          "--n-td-font-size": "1rem",
          "--n-td-padding": "0.5rem 0.75rem",
          "--n-td-text-color": "var(--smoe-gray-700)",
          "--n-th-color-modal": "var(--smoe-primary-50)",
          "--n-th-color": "var(--smoe-primary-50)",
          "--n-th-font-size": "0.875rem",
          "--n-th-font-weight": "600",
        };
      case "answer":
        return {
          ...baseStyle,
          "--n-border-color-modal": "var(--smoe-border)",
          "--n-border-color": "var(--smoe-border)",
          "--n-border-radius": "0.5rem",
          "--n-td-font-size": "1rem",
          "--n-td-padding": "0",
          "--n-td-text-color": "var(--smoe-gray-700)",
        };
      default: // list
        return {
          ...baseStyle,
          "--n-border-color-modal": "var(--smoe-border)",
          "--n-border-color": "var(--smoe-border)",
          "--n-border-radius": "0",
          "--n-td-font-size": "1rem",
          "--n-td-padding": "0.5rem 0.75rem",
          "--n-td-text-color": "var(--smoe-gray-700)",
          "--n-th-color-modal": "var(--smoe-primary-50)",
          "--n-th-color": "var(--smoe-primary-50)",
          "--n-th-font-size": "0.875rem",
          "--n-th-font-weight": "600",
        };
    }
  });
</script>

<template>
  <div :class="containerClass">
    <n-flex
      :wrap="false"
      align="flex-end"
      justify="space-between"
      class="mb-4"
      v-if="total != null || $slots.tableRightTop"
    >
      <p v-if="total != null" class="font-bold text-gray-700">
        총 <strong class="text-primary-500">{{ total }}</strong
        >{{ totalCountUnit }}
      </p>
      <slot name="table-right-top"></slot>
    </n-flex>
    <n-table
      class="ui-table"
      :bordered="(variant === 'bordered') | (variant === 'answer')"
      :single-line="variant === 'list'"
      :class="[
        { [`align-${align}`]: align },
        { [variant]: variant },
        { clickable: clickable },
      ]"
      :style="tableStyle"
      v-bind="$attrs"
    >
      <slot />
    </n-table>
  </div>
</template>

<style lang="scss">
  .n-table.ui-table {
    &.list {
      border-top: 1px solid var(--smoe-black);
    }
    &.bordered {
      border-radius: 0.5rem;
    }
    &.answer {
      .answer-cell {
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        &.unsolved {
          .answer-th,
          .answer-td {
            color: var(--smoe-red-500);
          }
        }
      }
      .answer-th,
      .answer-td {
        text-align: center;
      }
      .answer-th {
        flex: auto 0 0;
        padding: 1rem 0;
        background: var(--smoe-primary-50);
      }
      .answer-td {
        flex: 1;
        min-width: 1px;
        padding: 1rem 0;
        border-top: 1px solid var(--smoe-border);
      }
    }
    &.clickable {
      tbody {
        tr {
          cursor: pointer;
          &:hover {
            td {
              background-color: var(--smoe-gray-50);
            }
          }
        }
      }
    }
    th,
    td {
      word-break: break-word;
      white-space: normal;
      .n-button.ui-button {
        min-width: 60px;
      }
    }
    th {
      white-space: pre-line;
      font-size: var(--n-th-font-size, 1rem);
      &.required {
        &:after {
          content: " *";
          color: var(--smoe-primary-500);
        }
      }
    }
    td {
      font-size: var(--n-td-font-size, 1rem);
      height: 56px;
    }
    &.align-left {
      th,
      td {
        text-align: left;
      }
    }
    &.align-center {
      th,
      td {
        text-align: center;
      }
    }
    &.n-table--bordered tbody tr:last-child th {
      border-bottom: 0;
    }
  }
</style>
